import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { inHotelProfilePage } from '../../../shared/utils/UtilsHelper';
import { getAdditionalBookingParamsCssClasses, getSearchBarStyle } from './FlexiSearchBarWrapper.utils';
import { URLService } from 'Services';
export function useGetFlexiSearchBarWrapperHooks(props, intl) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z;
    const { 
    // core identifiers and configuration
    id, configuration, channelId, 
    // search and data
    search, promoCode, searchResults, settings, 
    // ui state and behavior
    isShown, isMobile, sticky, isLandingPage, shouldOpenOccupancySelector, isBannerLoaded, cookieBanner, 
    // layout and styling
    bannerSettings, headerId, searchBoxInitTrackingData, 
    // property and constraints
    propertyCode, currentLanguage, currentRoom, currentCurrency, unavailableDates = [], 
    // external services and utilities
    trustYouIdFromRedux, trustYouId, getQAData, onClose, scrollTo, trackEvent = () => { }, useWindowSize = () => ({ width: undefined, height: undefined }), 
    // library functions
    getKubeStyling, getTempSearchParams, 
    // change handlers - basic
    onChangeDates, onChangePromoCode, onChangeCurrency, onChangeLanguage, 
    // change handlers - occupancy
    onChangeRoomsQuantity, onRemoveRoom, onChangeAdults, onChangeChildren, onChangeChildrenAge, 
    // search and navigation
    destinationSelected, onSearchBarClick, getUrlParams, enableUpdateResultsButton, 
    // property settings
    hasAvailabilityIndependentRatePlan, } = props;
    // state management
    const [isMyBookingOpen, setIsMyBookingOpen] = useState(false);
    const [searchBarStyle, setSearchBarStyle] = useState(getSearchBarStyle(bannerSettings, undefined, headerId));
    // computed values
    const windowSize = useWindowSize();
    const effectiveTrustYouId = trustYouIdFromRedux || trustYouId || '';
    const searchBarVisibilityBreakpoint = 1024;
    const hideSearchBar = isMyBookingOpen && (((_a = windowSize.width) !== null && _a !== void 0 ? _a : 0) <= searchBarVisibilityBreakpoint);
    const hasSpecialCodes = (settings === null || settings === void 0 ? void 0 : settings.promotionalCodeEnabled) || false;
    const isMultiProperty = (settings === null || settings === void 0 ? void 0 : settings.isMultiProperty) || false;
    const propertyName = (settings === null || settings === void 0 ? void 0 : settings.propertyCode) || '';
    const additionalBookingParamClasses = getAdditionalBookingParamsCssClasses({
        hasSpecialCodes: hasSpecialCodes || false,
        isLandingPage: !!isLandingPage,
    });
    // effects for initialization and window management
    useEffect(() => {
        trackEvent('searchBoxInit', searchBoxInitTrackingData, true);
    }, [searchBoxInitTrackingData, trackEvent]);
    useEffect(() => {
        setSearchBarStyle(getSearchBarStyle(bannerSettings, windowSize.width, headerId));
    }, [bannerSettings, windowSize.width, headerId]);
    useEffect(() => {
        const handleWindowResize = () => {
            setSearchBarStyle(getSearchBarStyle(bannerSettings, window.innerWidth, headerId));
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [bannerSettings, headerId]);
    // trustyou callback
    const onShowTrustYouMetaReview = useCallback(() => trackEvent('viewTrustYouReviews', { propertyCode, trustYouId: effectiveTrustYouId, propertyName }), [propertyCode, effectiveTrustYouId, propertyName, trackEvent]);
    // close handler
    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    // search results handler
    const handleSearchResults = useCallback((searchParams, scroll) => {
        if (onSearchBarClick && destinationSelected) {
            let finalDestination;
            const params = getUrlParams(id, true);
            if (destinationSelected.type === 'hotel') {
                finalDestination = destinationSelected.value;
            }
            if (destinationSelected.type === 'city') {
                finalDestination = `${destinationSelected.countryEnglishName}/${destinationSelected.englishValue}`;
            }
            if (destinationSelected.type === 'country') {
                finalDestination = destinationSelected.englishValue;
            }
            onSearchBarClick(finalDestination, destinationSelected.type, params);
        }
        else {
            const params = getUrlParams(id, false);
            const isConnected = inHotelProfilePage() || (configuration === null || configuration === void 0 ? void 0 : configuration.isConnected);
            searchResults(id, isConnected, configuration, destinationSelected, params, isMultiProperty, settings === null || settings === void 0 ? void 0 : settings.hotelsFilters);
        }
    }, [onSearchBarClick, destinationSelected, getUrlParams, id, searchResults, configuration, isMultiProperty, settings]);
    // wrapped change handlers - occupancy
    const wrappedOnChangeRoomsQuantity = useCallback((quantity) => {
        onChangeRoomsQuantity === null || onChangeRoomsQuantity === void 0 ? void 0 : onChangeRoomsQuantity(id, quantity);
        enableUpdateResultsButton === null || enableUpdateResultsButton === void 0 ? void 0 : enableUpdateResultsButton();
    }, [onChangeRoomsQuantity, id, enableUpdateResultsButton]);
    const wrappedOnRemoveRoom = useCallback((index) => {
        onRemoveRoom === null || onRemoveRoom === void 0 ? void 0 : onRemoveRoom(id, index);
    }, [onRemoveRoom, id]);
    const wrappedOnChangeAdults = useCallback((roomIndex, adults) => {
        onChangeAdults === null || onChangeAdults === void 0 ? void 0 : onChangeAdults(id, roomIndex, adults);
        enableUpdateResultsButton === null || enableUpdateResultsButton === void 0 ? void 0 : enableUpdateResultsButton();
    }, [onChangeAdults, id, enableUpdateResultsButton]);
    const wrappedOnChangeChildren = useCallback((roomIndex, numberOfChildren) => {
        onChangeChildren === null || onChangeChildren === void 0 ? void 0 : onChangeChildren(id, roomIndex, numberOfChildren);
    }, [onChangeChildren, id]);
    const wrappedOnChangeChildrenAge = useCallback((roomIndex, childIndex, age) => {
        onChangeChildrenAge === null || onChangeChildrenAge === void 0 ? void 0 : onChangeChildrenAge(id, roomIndex, childIndex, age);
    }, [onChangeChildrenAge, id]);
    // wrapped change handlers - basic
    const wrappedOnChangePromoCode = useCallback((promotionalCode) => {
        onChangePromoCode === null || onChangePromoCode === void 0 ? void 0 : onChangePromoCode(id, promotionalCode);
    }, [onChangePromoCode, id]);
    const wrappedOnChangeCurrency = useCallback((currency) => {
        onChangeCurrency === null || onChangeCurrency === void 0 ? void 0 : onChangeCurrency(id, currency, isMultiProperty);
    }, [onChangeCurrency, id, isMultiProperty]);
    const wrappedOnChangeLanguage = useCallback((lang) => {
        onChangeLanguage === null || onChangeLanguage === void 0 ? void 0 : onChangeLanguage(id, lang, isMultiProperty, channelId || '', propertyCode || '');
    }, [onChangeLanguage, id, isMultiProperty, channelId, propertyCode]);
    const wrappedOnChangeDates = useCallback((dateRangeParams) => {
        if (onChangeDates && dateRangeParams && id) {
            const dates = {
                startDate: dateRangeParams.startDate,
                endDate: dateRangeParams.endDate,
                fromAction: 'calendar',
            };
            onChangeDates(id, dates);
            enableUpdateResultsButton === null || enableUpdateResultsButton === void 0 ? void 0 : enableUpdateResultsButton();
        }
    }, [onChangeDates, id, enableUpdateResultsButton]);
    // component props objects
    const trustYouWidgetProps = {
        language: (_b = currentLanguage !== null && currentLanguage !== void 0 ? currentLanguage : search === null || search === void 0 ? void 0 : search.language) !== null && _b !== void 0 ? _b : '',
        propertyCode: propertyCode !== null && propertyCode !== void 0 ? propertyCode : undefined,
        trustYouId: effectiveTrustYouId,
        queryParams: props.queryParams,
        onShowTrustYouMetaReview,
    };
    const flexiSearchBarProps = {
        // channel settings
        channelSettings: {
            id: (_c = settings === null || settings === void 0 ? void 0 : settings.channelId) !== null && _c !== void 0 ? _c : '',
            currencyDisplay: (_d = settings === null || settings === void 0 ? void 0 : settings.currencyDisplay) !== null && _d !== void 0 ? _d : 'symbol',
            maxRoomsPerBooking: (_e = settings === null || settings === void 0 ? void 0 : settings.maxRoomsPerBooking) !== null && _e !== void 0 ? _e : 0,
            calendarPrices: (_f = settings === null || settings === void 0 ? void 0 : settings.calendarPrices) !== null && _f !== void 0 ? _f : '',
            multiProperty: isMultiProperty !== null && isMultiProperty !== void 0 ? isMultiProperty : false,
            minLengthOfStay: (_g = settings === null || settings === void 0 ? void 0 : settings.minLengthOfStay) !== null && _g !== void 0 ? _g : 0,
            maxLengthOfStay: (_h = settings === null || settings === void 0 ? void 0 : settings.maxLengthOfStay) !== null && _h !== void 0 ? _h : 0,
            minNightOffset: (_j = settings === null || settings === void 0 ? void 0 : settings.minNightOffset) !== null && _j !== void 0 ? _j : 0,
        },
        // property information
        propertyInfo: {
            propertyCode: (_k = settings === null || settings === void 0 ? void 0 : settings.propertyCode) !== null && _k !== void 0 ? _k : '',
            hasAvailabilityIndependentRatePlan,
            minOccupancy: (_l = settings === null || settings === void 0 ? void 0 : settings.minOccupancy) !== null && _l !== void 0 ? _l : 0,
            maxChildAge: (_m = settings === null || settings === void 0 ? void 0 : settings.maxChildAge) !== null && _m !== void 0 ? _m : 0,
            minChildAge: (_o = settings === null || settings === void 0 ? void 0 : settings.minChildAge) !== null && _o !== void 0 ? _o : 0,
            maxOccupancy: (_p = settings === null || settings === void 0 ? void 0 : settings.maxOccupancy) !== null && _p !== void 0 ? _p : 0,
            childrenEnabled: (_q = settings === null || settings === void 0 ? void 0 : settings.childrenEnabled) !== null && _q !== void 0 ? _q : false,
            maxCheckOutDate: (_r = settings === null || settings === void 0 ? void 0 : settings.maxCheckoutDate) !== null && _r !== void 0 ? _r : moment().add(1, 'year').toString(),
        },
        // search parameters
        searchParamsTemp: {
            checkIn: (_s = search === null || search === void 0 ? void 0 : search.checkIn) !== null && _s !== void 0 ? _s : '',
            checkOut: (_t = search === null || search === void 0 ? void 0 : search.checkOut) !== null && _t !== void 0 ? _t : '',
            occupancy: (_u = search === null || search === void 0 ? void 0 : search.occupancy) !== null && _u !== void 0 ? _u : [{
                    adults: 2,
                    children: [],
                }],
            promotionalCode: promoCode !== null && promoCode !== void 0 ? promoCode : '',
        },
        // ui state
        updateResultsEnabled: true,
        currentLanguage: (_v = currentLanguage !== null && currentLanguage !== void 0 ? currentLanguage : search === null || search === void 0 ? void 0 : search.language) !== null && _v !== void 0 ? _v : '',
        isShown: isShown !== null && isShown !== void 0 ? isShown : false,
        isMobile: isMobile !== null && isMobile !== void 0 ? isMobile : false,
        shouldOpenOccupancySelector: shouldOpenOccupancySelector !== null && shouldOpenOccupancySelector !== void 0 ? shouldOpenOccupancySelector : false,
        isBannerLoaded: isBannerLoaded !== null && isBannerLoaded !== void 0 ? isBannerLoaded : false,
        isLandingPage: isLandingPage !== null && isLandingPage !== void 0 ? isLandingPage : false,
        sticky: sticky !== null && sticky !== void 0 ? sticky : false,
        isMapPage: false,
        // data and constraints
        cookieBanner: cookieBanner !== null && cookieBanner !== void 0 ? cookieBanner : { height: 0 },
        unavailableDates: unavailableDates !== null && unavailableDates !== void 0 ? unavailableDates : [],
        maxOccupancy: (_w = settings === null || settings === void 0 ? void 0 : settings.maxOccupancy) !== null && _w !== void 0 ? _w : null,
        currentRoom,
        currency: (_x = currentCurrency !== null && currentCurrency !== void 0 ? currentCurrency : search === null || search === void 0 ? void 0 : search.currency) !== null && _x !== void 0 ? _x : {},
        hasSpecialCodes: hasSpecialCodes !== null && hasSpecialCodes !== void 0 ? hasSpecialCodes : false,
        headerId: headerId !== null && headerId !== void 0 ? headerId : '',
        // change handlers
        updateResults: handleSearchResults,
        onChangeDates: wrappedOnChangeDates,
        onChangeRoomsQuantity: wrappedOnChangeRoomsQuantity,
        onRemoveRoom: wrappedOnRemoveRoom,
        onChangeAdults: wrappedOnChangeAdults,
        onChangeChildren: wrappedOnChangeChildren,
        onChangeChildrenAge: wrappedOnChangeChildrenAge,
        onChangePromotionalCode: wrappedOnChangePromoCode,
        onChangeCurrency: wrappedOnChangeCurrency,
        onChangeLanguage: wrappedOnChangeLanguage,
        onClose: handleClose,
        // utility functions
        enableUpdateResultsButton: enableUpdateResultsButton !== null && enableUpdateResultsButton !== void 0 ? enableUpdateResultsButton : (() => { }),
        getQAData: getQAData !== null && getQAData !== void 0 ? getQAData : (() => ({})),
        getKubeStyling,
        getTempSearchParams,
        scrollTo,
        // api endpoints
        calendarPricesURL: URLService.externals.getCalendarPricesURL(),
        availabilitiesURL: URLService.externals.getAvailabilitiesURL(propertyCode),
        // labels
        doneButtonLabel: intl.formatMessage({
            id: 'bf.doneButtonDatePicker',
            defaultMessage: 'bf.doneButtonDatePicker',
        }),
        resetButtonLabel: intl.formatMessage({
            id: 'nemo.reset',
            defaultMessage: 'nemo.reset',
        }),
        cancelButtonLabel: intl.formatMessage({
            id: 'bf.cancel',
            defaultMessage: 'bf.cancel',
        }),
        // optional handlers and data
        calendarLocale: (_z = (_y = search === null || search === void 0 ? void 0 : search.language) !== null && _y !== void 0 ? _y : settings === null || settings === void 0 ? void 0 : settings.language) !== null && _z !== void 0 ? _z : null,
        onOccupancyToggle: undefined,
        onCalendarToggle: undefined,
        taxInfo: undefined,
        checkoutSummary: undefined,
    };
    // return hook values
    return {
        isMyBookingOpen,
        setIsMyBookingOpen,
        searchBarStyle,
        hideSearchBar,
        hasSpecialCodes: hasSpecialCodes || false,
        additionalBookingParamClasses,
        isMultiProperty,
        effectiveTrustYouId,
        handleClose,
        flexiSearchBarProps,
        trustYouWidgetProps,
        handleSearchResults,
    };
}
